import { makeRoute } from "./route";
import { Serializers } from "./types";
import * as Safe from "safe-portals";
import * as perm from "./core_permissions";

export const ping = makeRoute("/ping", "public", Safe.nothing, Safe.str);

export const adminPing = makeRoute("/admin/ping", [], Safe.nothing, Safe.str);

export const getAbTestSampleSize = makeRoute("/admin/api/get_ab_test_sample_size", [], Safe.nothing, Safe.int);

export const abTestSampleSizeOverridesSerializer = Safe.obj({
  donate_actions: Safe.int,
  petition_actions: Safe.int,
  html_actions: Safe.int,
  social_share_actions: Safe.int,
  survey_actions: Safe.int,
});

export const getAbTestSampleSizeOverrides = makeRoute(
  "/admin/api/get_ab_test_sample_size_overrides",
  [],
  Safe.nothing,
  abTestSampleSizeOverridesSerializer
);

export const whoAmI = makeRoute(
  "/admin/who_am_i",
  "public",
  Safe.nothing,
  Safe.optional(
    Safe.obj({
      email: Safe.str,
      name: Safe.str,
      permissions: Safe.array(Safe.str),
    })
  )
);

// we make all flags optional, so we do not have to update the existing settings with a migration
// when we add a new one.
export const featureFlagsSerializer = Safe.obj({
  create_journey_from_template: Safe.optional(Safe.bool),
  use_beamer_release_log: Safe.optional(Safe.bool),
  beamer_product_id: Safe.optional(Safe.str),
});

const coreSettingsSerializer = Safe.obj({
  region: Safe.str,
  ab_test_sample_size: Safe.int,
  ab_test_sample_size_overrides: abTestSampleSizeOverridesSerializer,
  default_from_email: Safe.str,
  aws_image_processing_url: Safe.str,
  twitter_handle: Safe.str,
  default_consent_configs: Safe.array(Safe.uuid),
  permitted_insecure_redirect_url_prefixes: Safe.array(Safe.str),
  feature_flags: featureFlagsSerializer,
});

export type CoreSettings = Safe.TypeIn<typeof coreSettingsSerializer>;

export const getCoreSettings = makeRoute(
  "/admin/api/get_core_settings",
  [perm.VIEW_SETTINGS],
  Safe.nothing,
  coreSettingsSerializer
);

export const setCoreSettings = makeRoute(
  "/admin/api/set_core_settings",
  [perm.UPDATE_SETTINGS],
  coreSettingsSerializer,
  Safe.nothing
);

export const getConditionInputTypes = makeRoute(
  "/admin/api/condition_input_types",
  [],
  Safe.nothing,
  Safe.array(
    Safe.obj({
      internal_name: Safe.str,
      label: Safe.str,
    })
  )
);

export const erasePersonalData = makeRoute(
  "/admin/api/personal_data/erase",
  [],
  Safe.obj({
    emails: Safe.array(Safe.str),
  }),
  Safe.nothing
);

export const getConsentTexts = makeRoute(
  "/admin/api/consent/texts",
  [],
  Safe.nothing,
  Safe.array(
    Safe.obj({
      public_id: Safe.str,
      consent_short_text: Safe.str,
      full_legal_text_link: Safe.str,
      created_at: Safe.dateIso,
      updated_at: Safe.dateIso,
    })
  )
);

export const getConsentConfigs = makeRoute(
  "/admin/api/consent/configs",
  [perm.VIEW_SETTINGS],
  Safe.partial_obj({
    by_guid: Safe.str,
  }),
  Safe.array(Serializers.consentConfig)
);

export const saveConsentConfig = makeRoute(
  "/admin/api/consent/configs/save",
  [perm.UPDATE_SETTINGS],
  Serializers.consentConfig,
  Safe.nothing
);

export const saveJourney = makeRoute(
  "/admin/api/journey/save",
  [],
  // arguments
  Serializers.journey,
  // response (either success (nothing) or an error string)
  Safe.obj({
    error: Safe.optional(Safe.str),
  })
);

export const deleteJourney = makeRoute(
  "/admin/api/journey/delete",
  [],
  // arguments
  Safe.obj({ guid: Safe.uuid }),
  // response
  Safe.nothing
);

export const makeJourneyLive = makeRoute("/admin/api/journey/go_live", [], Safe.obj({ guid: Safe.uuid }), Safe.nothing);

export const getJourneyList = makeRoute(
  "/admin/api/journeys",
  [],
  Safe.obj({
    page: Safe.int,
    rowsPerPage: Safe.int,
    searchTerm: Safe.optional(Safe.str),
  }),
  Safe.obj({
    items: Safe.array(Serializers.journeyListItem),
    total: Safe.int,
  })
);

/**
 * Return public journeys as a paged list.
 * Optional search available.
 */
export const getPublicJourneyList = makeRoute(
  "/api/journeys",
  "public",
  Safe.obj({
    page: Safe.int,
    rowsPerPage: Safe.int,
    searchTerm: Safe.optional(Safe.str),
  }),
  Safe.obj({
    items: Safe.array(Serializers.journeyListPublicItem),
    total: Safe.int,
  })
);

export const startJourney = makeRoute(
  "/api/journey/start",
  "public",
  // arguents: current step guid and journey context
  Safe.obj({
    journey_guid: Safe.uuid,
    context: Serializers.journeyContext,
  }),
  // response: next action step
  Serializers.publicJourneyStepResponse
);

export const takeNextJourneyStep = makeRoute(
  "/api/journey/next_step",
  "public",
  // arguents: current step guid and journey context
  Safe.obj({
    performed_action: Safe.bool,
    journey_guid: Safe.str,
    from_action_guid: Safe.str, // undefined means starting journey
    context: Serializers.journeyContext,
    consents: Safe.array(Serializers.consent),
  }),
  // response: next action step
  Serializers.publicJourneyStepResponse
);

//export async function getJourneyBySlug(slug: string): Promise<Journey | 'NotFound' | 'ServerError'> {
export const getJourneyBySlug = makeRoute(
  "/admin/api/journey/by-slug",
  [],
  Safe.obj({
    slug: Safe.str,
  }),
  Safe.optional(Serializers.journey)
);

export const getJourneyByGuid = makeRoute(
  "/admin/api/journey/by-guid",
  [],
  Safe.obj({
    guid: Safe.str,
  }),
  Safe.optional(Serializers.journey)
);

export const saveJourneyTemplate = makeRoute(
  "/admin/api/journeytemplate/save",
  [],
  Serializers.journeyTemplate,
  Safe.obj({
    error: Safe.optional(Safe.str),
  })
);

export const deleteJourneyTemplate = makeRoute(
  "/admin/api/journeytemplate/delete",
  [],
  // arguments
  Safe.obj({ journey_guid: Safe.uuid }),
  // response
  Safe.nothing
);

export const getJourneyTemplates = makeRoute(
  "/admin/api/journey/templates",
  [],
  Safe.nothing,
  Safe.optional(Serializers.journeyTemplates)
);

export const getJourneyActionStats = makeRoute(
  "/admin/api/journey/action-stats",
  [],
  Safe.obj({
    guid: Safe.str,
  }),
  Safe.array(Serializers.journeyActionStats)
);

export const reportError = makeRoute(
  "/api/report-error",
  "public",
  Safe.obj({
    userAgent: Safe.str,
    msg: Safe.str,
    url: Safe.str,
    lineNo: Safe.int,
    columnNo: Safe.int,
    error: Safe.str,
  }),
  Safe.nothing
);

export const reportVideoRecorderError = makeRoute(
  "/api/report-video-recorder-error",
  "public",
  Safe.obj({
    msg: Safe.str,
    status: Safe.oneOf("NotAllowed", "BrowserUnsupported", "SetupFailed"),
  }),
  Safe.nothing
);

export const deleteUser = makeRoute(
  "/admin/api/delete_user",
  [perm.UPDATE_SETTINGS],
  Safe.obj({
    user_guid: Safe.uuid,
  }),
  Safe.nothing
);

export const saveUser = makeRoute(
  "/admin/api/save_user",
  [perm.UPDATE_SETTINGS],
  Safe.obj({
    guid: Safe.uuid,
    name: Safe.str,
    email: Safe.optional(Safe.str),
    roles: Safe.array(
      Safe.obj({
        role_guid: Safe.uuid,
        user_has: Safe.bool,
      })
    ),
  }),
  Safe.nothing
);

export const getUsers = makeRoute(
  "/admin/api/get_users",
  [perm.VIEW_SETTINGS],
  Safe.obj({
    page: Safe.int,
    rowsPerPage: Safe.int,
    search: Safe.optional(Safe.str),
    orderBy: Safe.optional(
      Safe.partial_obj({
        field: Safe.str,
        direction: Safe.oneOf("asc", "desc"),
      })
    ),
  }),
  Safe.array(Serializers.user)
);

export const getUserRoles = makeRoute(
  "/admin/api/get_user_roles",
  [perm.VIEW_SETTINGS],
  Safe.obj({
    user_guid: Safe.uuid,
  }),
  Safe.array(
    Safe.obj({
      role_guid: Safe.uuid,
      role_name: Safe.str,
      user_has: Safe.bool,
    })
  )
);

export const getRoles = makeRoute(
  "/admin/api/get_roles",
  [perm.VIEW_SETTINGS],
  Safe.obj({
    page: Safe.int,
    rowsPerPage: Safe.int,
    search: Safe.optional(Safe.str),
  }),
  Safe.array(Serializers.role)
);

export const saveRole = makeRoute(
  "/admin/api/save_role",
  [perm.UPDATE_SETTINGS],
  Safe.obj({
    guid: Safe.uuid,
    name: Safe.str,
    permissions: Safe.array(Safe.str),
  }),
  Safe.nothing
);

export const deleteRole = makeRoute(
  "/admin/api/delete_role",
  [perm.UPDATE_SETTINGS],
  Safe.obj({
    role_guid: Safe.uuid,
  }),
  Safe.nothing
);

export const getFilestoreStats = makeRoute(
  "/admin/api/filestore/stats",
  [],
  // arguments
  Safe.nothing,
  // response
  Serializers.filestoreStats
);

export const getFilestoreSettings = makeRoute(
  "/admin/api/filestore/get_settings",
  [perm.VIEW_SETTINGS],
  Safe.nothing,
  Safe.obj({
    aws_elastic_transcode_pipeline_id: Safe.optional(Safe.str),
    journey_assets_s3_bucket: Safe.str,
  })
);

export const setFilestoreSettings = makeRoute(
  "/admin/api/filestore/set_settings",
  [perm.UPDATE_SETTINGS],
  Safe.obj({
    aws_elastic_transcode_pipeline_id: Safe.optional(Safe.str),
    journey_assets_s3_bucket: Safe.str,
  }),
  Safe.nothing
);

export const getJourneyAssetUploadUrl = makeRoute(
  "/admin/api/filestore/get_journey_asset_upload_url",
  [],
  Safe.obj({
    filename: Safe.str,
    mime_type: Safe.str,
  }),
  Safe.obj({ url: Safe.str, fields: Safe.raw })
);

export const getShortenedUrl = makeRoute(
  "/admin/api/get_shortened_url",
  [],
  Safe.obj({
    longUrl: Safe.str,
  }),
  Safe.obj({
    shortenedUrl: Safe.str,
    error: Safe.optional(Safe.str),
  })
);

export const getJourneyContinuation = makeRoute(
  "/api/get_journey_continuation",
  "public",
  Safe.obj({
    email: Safe.str,
  }),
  Safe.array(
    Safe.obj({
      og_image: Safe.str,
      name: Safe.str,
      slug: Safe.str,
      signature_count: Safe.int,
    })
  )
);

export const getThreeJourneys = makeRoute(
  "/api/get_three_journeys",
  "public",
  Safe.nothing,
  Safe.array(
    Safe.obj({
      og_image: Safe.str,
      name: Safe.str,
      slug: Safe.str,
      signature_count: Safe.int,
    })
  )
);
